// @ts-check

import '../css/animations.scss'

/**
 * @typedef {(
 *  'fade-in' |
 *  'slide-in-top' |
 *  'slide-in-bottom' |
 *  'slide-in-left' |
 *  'slide-in-right' |
 *  'ease-in-top' |
 *  'ease-in-bottom' |
 *  'ease-in-left' |
 *  'ease-in-right'
 * )} EnterEffect
 */

/**
 * @typedef {(
 *  'fade-out' |
 *  'slide-out-top' |
 *  'slide-out-bottom' |
 *  'slide-out-left' |
 *  'slide-out-right' |
 *  'ease-out-top' |
 *  'ease-out-bottom' |
 *  'ease-out-left' |
 *  'ease-out-right'
 * )} ExitEffect
 */

/**
 * @typedef {(
 *  'ease' |
 *  'ease-in' |
 *  'ease-out' |
 *  'ease-in-out' |
 *  'linear' |
 *  'step-start' |
 *  'step-end'
 * )} Easing
 */

/**
 * @template E effects
 * @typedef {Object} AnimationObject
 * @property {E} effect
 * @property {number} duration in milliseconds
 * @property {Easing} easing
 * @property {number} delay in milliseconds
 */

/**
 * @typedef {AnimationObject<EnterEffect>} EnterAnimationObject
 */

/**
 * @typedef {AnimationObject<ExitEffect>} ExitAnimationObject
 */

/**
 * @typedef {AnimationObject<EnterEffect|ExitEffect>} GenericAnimationObject
 */

/**
 *
 * @param {HTMLElement} element element to animate
 * @param {GenericAnimationObject} animation animation
 * @returns {Promise<void>} resolved once the animation has ended
 */
function animate(element, animation) {
  const classes = effectToClasslist(animation.effect)

  return new Promise((resolve) => {
    /**
     *
     * @param {Partial<TransitionEvent>} event
     */
    const onTransitionEnd = (event) => {
      if (event.target !== element) return
      element.removeEventListener('transitionend', onTransitionEnd)
      resolve()
    }

    // If duration is 0 transitionend will not fire, so we will call it later
    if (animation.duration !== 0)
      element.addEventListener('transitionend', onTransitionEnd)

    element.classList.add(...classes)
    requestAnimationFrame(() => {
      applyAnimationStyle(element, animation)
      requestAnimationFrame(() => {
        element.classList.add('end')

        // If duration is 0 transitionend will not fire, so we call it now
        if (animation.duration === 0)
          requestAnimationFrame(() => onTransitionEnd({ target: element }))
      })
    })
  })
}

/**
 * Cleans up CSS and style artifacts from an animation.
 * This is separate from the animate function because we might want to apply
 * several animation and only cleanup at the end.
 * @param {HTMLElement} element element to animate
 * @param {GenericAnimationObject} animation animation
 */
function cleanUpAnimation(element, animation) {
  const classes = effectToClasslist(animation.effect)
  resetAnimationStyle(element)
  element.classList.remove(...classes.concat('end'))
}

/**
 * Applies an animation to an element
 *
 * @param {HTMLElement} element element to animate
 * @param {GenericAnimationObject} animation
 */
function applyAnimationStyle(element, animation) {
  element.style.transitionDuration = animation.duration + 'ms'
  element.style.transitionTimingFunction = animation.easing
  element.style.transitionDelay = animation.delay + 'ms'
}

function resetAnimationStyle(element) {
  element.style.transitionDuration = '0s'
  element.style.transitionTimingFunction = 'liner'
  element.style.transitionDelay = '0s'
}

/**
 * Converts a PopupAnimation identifier into a classlist
 * @param {EnterEffect|ExitEffect} effect
 * @returns {string[]}
 */
function effectToClasslist(effect) {
  const PREFIX_CLASS = 'dot-embed-popup-animation'

  switch (effect) {
    case 'fade-in':
      return [`${PREFIX_CLASS}-fade-in`]
    case 'ease-in-top':
    case 'ease-in-bottom':
    case 'ease-in-left':
    case 'ease-in-right':
      return [`${PREFIX_CLASS}-${effect}`]
    case 'slide-in-top':
    case 'slide-in-bottom':
    case 'slide-in-left':
    case 'slide-in-right':
      return [`${PREFIX_CLASS}-${effect}`]

    case 'fade-out':
      return [`${PREFIX_CLASS}-fade-out`]
    case 'ease-out-top':
    case 'ease-out-bottom':
    case 'ease-out-left':
    case 'ease-out-right':
      return [`${PREFIX_CLASS}-${effect}`]
    case 'slide-out-top':
    case 'slide-out-bottom':
    case 'slide-out-left':
    case 'slide-out-right':
      return [`${PREFIX_CLASS}-${effect}`]
    default:
      throw Error(`effect ("${effect}") not supported`)
  }
}

export { animate, cleanUpAnimation }
